"use client";

import React, { useState, useMemo, useEffect } from "react";
import Image from "next/image";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { parseHtmlJson } from "@/utils/parseHtmlTextToJson";
import {
  useFetchProductCollection,
  useFetchProductCount,
} from "@/apis/queryFunctions/productFunctions";
import { useFetchDmnvts } from "@/apis/queryFunctions/categoryFunctions";
import Container from "@/components/Layouts/Container/Container";
import ProductCard from "@/components/Product/ProductCard";
import FilterBar from "@/components/Product/FilterBar";
import TopFilterBar from "@/components/Product/TopFilterBar";
import MobileFilterDrawer from "@/components/Product/MobileFilterDrawer";
import Pagination from "@/components/Product/Pagination";
import { useHeader } from "@/context/HeaderLayoutContext";
import { useFetchEcompage } from "@/apis/queryFunctions/ecompagesFunctions";
import { ProductProvider } from "@/context/ProductContext";
import productColors from "@/mockDatas/productColors.json";
import { Title, Description } from "@/components/Common/Typography";
import styles from "./sanpham.module.css";

const PAGE_SIZE = Number(process.env.COLLECTION_LOADING_NUMBER || 16);

function getSortObj(sortKey: string) {
  switch (sortKey) {
    case "price-asc":
      return { gia_ban_le: 1 };
    case "price-desc":
      return { gia_ban_le: -1 };
    case "name-asc":
      return { ten_vt: 1 };
    default:
      return { date_updated: -1 };
  }
}

interface ProductsClientProps {
  initialData?: any;
  seo?: any;
  locale?: string;
}

export default function ProductsClient({
  initialData,
  seo,
  locale = "vi",
}: ProductsClientProps) {
  const { setHeaderStyle } = useHeader();

  useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  //#region general data
  const pageData = initialData;

  const allAddOn1 = useMemo(() => {
    return Array.isArray(pageData?.add_on_data) &&
      pageData.add_on_data.length > 0
      ? pageData.add_on_data
      : [];
  }, [pageData]);

  const getAddOn1 = (keys: string[]) => {
    const lowerKeys = keys.map((k) => k.toLowerCase());
    return (
      allAddOn1.find((item: any) => {
        const title = (item?.title || "").toLowerCase();
        const summary = (item?.summary || "").toLowerCase();
        return lowerKeys.some(
          (k) => title === k || summary === k || title.includes(k)
        );
      }) || null
    );
  };

  const bannerRecord = getAddOn1(["banner"]);
  const catalogRecord = getAddOn1(["tat-ca-san-pham", "tat_ca_san_pham"]);

  const bannerBgImg = bannerRecord?.picture || seo?.picture;
  const bannerProductImg =
    bannerRecord?.picture_2 || seo?.picture_2 || "/images/tra.png";
  const decoLeftImg = bannerRecord?.picture_3 || seo?.picture_3;
  const decoRightImg = bannerRecord?.picture_4 || seo?.picture_4;
  const bannerTitle =
    bannerRecord?.summary || seo?.title || "Sản phẩm Việt Thiên";
  const bannerSubtitle =
    bannerRecord?.content_3 ||
    seo?.summary ||
    "Giải pháp xanh cho doanh nghiệp";
  const bannerDesc = bannerRecord?.content || seo?.content || "";

  const isBannerVisible = bannerRecord
    ? bannerRecord.shared !== false &&
      bannerRecord.shared !== "false" &&
      bannerRecord.shared !== 0 &&
      bannerRecord.shared !== "0"
    : true;

  const catalogTitle =
    catalogRecord?.summary ||
    (locale === "en" ? "All Products" : "Tất cả sản phẩm");
  const catalogSubtitle =
    catalogRecord?.content_2 ||
    (locale === "en"
      ? "Explore our comprehensive catalog of quality Viet Thien products"
      : "Khám phá trọn vẹn danh mục sản phẩm đa dạng và chất lượng của Việt Thiên");
  // Parse dynamic product config (allowed_ma_nganh_hang, group_definitions, categories) from catalogRecord.content
  const productConfig = useMemo(() => {
    const fromContent = parseHtmlJson(catalogRecord?.content);
    return fromContent || productColors;
  }, [catalogRecord?.content]);
  //#endregion

  //#region product list data
  const [page, setPage] = useState(1);
  const [sortQuery, setSortQuery] = useState("newest");
  const [selectedDmnvts, setSelectedDmnvts] = useState<string[]>([]);
  const [gridCols, setGridCols] = useState<3 | 4>(4);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Fetch all product groups (dmnvt) for filter + default fetch
  const { data: allDmnvts } = useFetchDmnvts({
    condition: { shared: true, status: true },
  });

  // Danh sách mã ngành hàng cho phép lấy từ catalogRecord.content (fallback về productColors.json)
  const allowedNganhHang = useMemo(() => {
    return Array.isArray(pageData?.allowed_ma_nganh_hang)
      ? pageData.allowed_ma_nganh_hang
      : Array.isArray(productConfig?.allowed_ma_nganh_hang)
        ? productConfig.allowed_ma_nganh_hang
        : ["TRA", "CAFE", "NS", "TPCB"];
  }, [pageData, productConfig]);

  // Danh sách định nghĩa nhóm ngành hàng từ catalogRecord.content (fallback về productColors.json)
  const groupDefinitions = useMemo(() => {
    return Array.isArray(productConfig?.group_definitions)
      ? productConfig.group_definitions
      : productColors?.group_definitions || [];
  }, [productConfig]);

  // All available category IDs for fetching (lọc theo các ngành hàng được phép)
  const allDmnvtIds = useMemo(() => {
    return (allDmnvts || [])
      .filter(
        (item: any) =>
          item?.ma_nganh_hang && allowedNganhHang.includes(item.ma_nganh_hang)
      )
      .map((item: any) => item._id);
  }, [allDmnvts, allowedNganhHang]);

  // Which IDs to actually use for fetching
  const activeDmnvtIds =
    selectedDmnvts.length > 0 ? selectedDmnvts : allDmnvtIds;

  // Options for fetching
  const fetchOptions = useMemo(
    () => ({
      page,
      sort: getSortObj(sortQuery),
      condition: {},
    }),
    [page, sortQuery]
  );

  const {
    data: products,
    isLoading,
    isFetched,
  } = useFetchProductCollection(fetchOptions, activeDmnvtIds);

  const { data: productCount } = useFetchProductCount({
    options: { condition: { ma_nvt: activeDmnvtIds } },
  });

  const totalPages = Math.max(1, Math.ceil((productCount || 0) / PAGE_SIZE));

  // Reset to page 1 on filter/sort change
  useEffect(() => {
    setPage(1);
  }, [selectedDmnvts, sortQuery]);

  const skeletons = useMemo(() => new Array(PAGE_SIZE).fill(null), []);

  const handleReset = () => {
    setSelectedDmnvts([]);
    setSortQuery("newest");
    setPage(1);
  };

  //#endregion

  return (
    <ProductProvider initialConfig={productConfig}>
      <div className={styles.wrapper}>
        {/* ═══ HERO BANNER ═══ */}
        {isBannerVisible && (
          <section className={styles.hero}>
            <div className={styles.heroBg}>
              {/* Background Image - Like GioiThieuClient */}
              {bannerBgImg && (
                <Image
                  src={appendImageUrlFromAPI({
                    src: bannerBgImg,
                    size: "2k",
                  })}
                  alt={bannerTitle}
                  fill
                  priority
                  sizes="100vw"
                  className={styles.bannerBgImage}
                />
              )}

              {/* Side Decorations */}
              {/* {decoLeftImg && (
              <div className={styles.sideDecoLeft}>
                <Image
                  src={appendImageUrlFromAPI({ src: decoLeftImg, size: "2k" })}
                  alt="Deco Left"
                  width={220}
                  height={220}
                  className={styles.decoImg}
                />
              </div>
            )}
            {decoRightImg && (
              <div className={styles.sideDecoRight}>
                <Image
                  src={appendImageUrlFromAPI({ src: decoRightImg, size: "2k" })}
                  alt="Deco Right"
                  width={220}
                  height={220}
                  className={styles.decoImg}
                />
              </div>
            )} */}

              <div className={styles.heroMainContent}>
                <Title
                  as="h1"
                  size="large"
                  color="#ffffff"
                  align="center"
                  className={styles.heroTitle}
                >
                  {bannerTitle}
                </Title>
                {bannerDesc && (
                  <Description
                    as="div"
                    size="medium"
                    color="rgba(255, 255, 255, 0.95)"
                    align="center"
                    className={styles.heroDesc}
                    dangerouslySetInnerHTML={{ __html: bannerDesc }}
                  />
                )}
              </div>

              {bannerProductImg && (
                <div className={styles.heroDeco}>
                  <Image
                    src={appendImageUrlFromAPI({
                      src: bannerProductImg,
                      size: "2k",
                    })}
                    alt={bannerTitle}
                    width={1000}
                    height={500}
                    className={styles.heroImg}
                  />
                </div>
              )}
            </div>

            <div className={styles.heroBottomSpacer} />
          </section>
        )}

        {/* ═══ LISTING HEADER ═══ */}
        <section className={styles.listingHeader}>
          <h2 className={styles.listingTitle}>{catalogTitle}</h2>
          <p className={styles.listingSubtitle}>{catalogSubtitle}</p>
        </section>

        {/* ═══ PRODUCT GRID ═══ */}
        <Container fluid>
          {/* Top Filter Bar / Action Toolbar */}
          <TopFilterBar
            totalCount={productCount || 0}
            visibleCount={products?.length || 0}
            selectedDmnvts={selectedDmnvts}
            onDmnvtChange={setSelectedDmnvts}
            allDmnvts={allDmnvts || []}
            allowedNganhHang={allowedNganhHang}
            groupDefinitions={groupDefinitions}
            sortQuery={sortQuery}
            onSortChange={setSortQuery}
            gridCols={gridCols}
            onGridColsChange={setGridCols}
            onOpenMobileFilter={() => setIsMobileDrawerOpen(true)}
            onResetAll={handleReset}
            locale={locale}
          />

          <div className={styles.layout}>
            {/* Sidebar (Desktop) */}
            <div className={styles.sidebarCol}>
              <FilterBar
                selectedDmnvts={selectedDmnvts}
                onDmnvtChange={setSelectedDmnvts}
                onReset={handleReset}
                allowedNganhHang={allowedNganhHang}
                groupDefinitions={groupDefinitions}
                locale={locale}
              />
            </div>

            {/* Main content */}
            <div className={styles.mainCol}>
              {/* Products grid */}
              <div
                className={`${styles.grid} ${gridCols === 3 ? styles.gridCols3 : styles.gridCols4}`}
              >
                {isFetched && (!products || products.length === 0) && (
                  <div className={styles.empty}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ccc"
                      strokeWidth="1.5"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M8 15s1.5-2 4-2 4 2 4 2" />
                      <line x1="9" y1="9" x2="9.01" y2="9" />
                      <line x1="15" y1="9" x2="15.01" y2="9" />
                    </svg>
                    <p>
                      {locale === "en"
                        ? "No products found"
                        : "Không tìm thấy sản phẩm"}
                    </p>
                  </div>
                )}

                {(isLoading ? skeletons : products || []).map(
                  (product: any, i: number) => (
                    <div
                      key={product?._id ?? `skeleton-${i}`}
                      className={styles.gridItem}
                    >
                      <ProductCard
                        product={product}
                        isLoading={isLoading}
                        colorConfig={productConfig}
                        locale={locale}
                      />
                    </div>
                  )
                )}
              </div>

              {/* Pagination */}
              <Pagination
                page={page}
                total={totalPages}
                onChange={setPage}
                totalCount={productCount || 0}
                visibleCount={products?.length || 0}
                locale={locale}
              />
            </div>
          </div>

          {/* Mobile Filter Drawer */}
          <MobileFilterDrawer
            isOpen={isMobileDrawerOpen}
            onClose={() => setIsMobileDrawerOpen(false)}
            selectedDmnvts={selectedDmnvts}
            onDmnvtChange={setSelectedDmnvts}
            onReset={handleReset}
            allowedNganhHang={allowedNganhHang}
            groupDefinitions={groupDefinitions}
            productCount={productCount || 0}
            locale={locale}
          />
        </Container>
      </div>
    </ProductProvider>
  );
}
